import { useApolloClient } from '@apollo/client';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import PermIdentityOutlinedIcon from '@mui/icons-material/PermIdentityOutlined';
import { Box, Drawer, IconButton, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { CommonModal, LOGOUT_USER } from '@watter/shared-components';
import { signOut } from 'aws-amplify/auth';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { client } from '../../service/apolloClient';
import { revertAll } from '../../store/actions';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { setUnreadNotificationsCount } from '../../store/reducers/notificationsSlice';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useNotificationSocket } from '../../web-socket/notifications-use-socket';
import { WSInstance } from '../../web-socket/web-socket-service';
import HeaterDetailsIcon from '../icons/heater-details-icon';
import { LogoIcon } from '../icons/logo-icon';
import { SideMenuStyles } from './sidemenu-styles';

interface IProps {
    menuOpen: any;
    menuClose: any;
}
export const SideMenu: React.FC<IProps> = ({ menuOpen, menuClose }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [modalOpen, setModalOpen] = React.useState<boolean>(false);
    const [activeMenu, setActiveMenu] = React.useState<string>(NavigationConfig.Home);
    const metricsData = useSelector((state: any) => state.heater.metricsData);
    const apolloClient = useApolloClient();
    const location = useLocation();
    const handleLogout = async () => {
        dispatch(showLoader());
        WSInstance.disconnect(); // socket disconnect
        await client.mutate({
            mutation: LOGOUT_USER,
        });
        setTimeout(async () => {
            await signOut()
                .then(async (res: any) => {
                    console.warn(res, '==> res in signout');
                    // calling graphQL logout API
                    // await client.mutate({
                    //     mutation: LOGOUT_USER,
                    // });
                })
                .catch((err: any) => {
                    console.error('err SignOut: ', err);
                    dispatch(hideLoader());
                })
                .finally(() => {
                    dispatch(hideLoader());
                    dispatch(revertAll());

                    sessionStorage.clear();
                    apolloClient.clearStore();
                    navigate(NavigationConfig.Login);
                });
        }, 1000);
    };
    const pathNavigateClick = (value: any) => {
        navigate(value);
        setActiveMenu(value);
    };
    const MenuItems = React.useMemo(() => {
        const showHeaterMenu = metricsData?.machine_id;
        if (showHeaterMenu) {
            return [
                {
                    pathValue: NavigationConfig.Home,
                    title: t('dashboard'),
                    icon: <DashboardOutlinedIcon />,
                },
                {
                    pathValue: NavigationConfig.HeaterDetails,
                    title: t('watter_details'),
                    icon: <HeaterDetailsIcon />,
                },
                {
                    pathValue: NavigationConfig.ProfileSetting,
                    title: t('profile_settings'),
                    icon: <PermIdentityOutlinedIcon />,
                },
                {
                    pathValue: NavigationConfig.Contactus,
                    title: t('support'),
                    icon: <HeadsetMicOutlinedIcon />,
                },
            ];
        }
        return [
            {
                pathValue: NavigationConfig.Home,
                title: t('dashboard'),
                icon: <DashboardOutlinedIcon />,
            },

            {
                pathValue: NavigationConfig.ProfileSetting,
                title: t('profile_settings'),
                icon: <PermIdentityOutlinedIcon />,
            },
            {
                pathValue: NavigationConfig.Contactus,
                title: t('support'),
                icon: <HeadsetMicOutlinedIcon />,
            },
        ];
    }, [t, metricsData]);

    React.useEffect(() => {
        const menuItem = MenuItems.find((item: any) => item.pathValue === location.pathname);
        setActiveMenu(menuItem?.pathValue || NavigationConfig.Home);
    }, [location.pathname, MenuItems]);

    const onNotificationCount = (data: any) => {
        console.warn('data from socket notification count side menu ==>', data);
        dispatch(setUnreadNotificationsCount(1));
    };
    useNotificationSocket(onNotificationCount);
    const navigateToDashboard = () => {
        navigate(NavigationConfig.Home);
        setActiveMenu(t('dashboard'));
    };
    const matches = useMediaQuery(theme => theme.breakpoints.down('lg'));
    const sidemenuContent = () => {
        return (
            <SideMenuStyles height="100%">
                <Box>
                    <Box className="logo" textAlign="center" onClick={() => navigateToDashboard()}>
                        <LogoIcon />
                    </Box>
                    <Stack className="listItems" height="100%" alignItems={{ xs: 'flex-start', lg: 'center' }}>
                        {MenuItems.map(items => (
                            <Tooltip title={items.title} arrow placement="right" key={items.pathValue}>
                                <Box
                                    key={items?.pathValue}
                                    display={{ xs: 'flex', lg: 'block' }}
                                    mb={{ xs: 2, lg: 0 }}
                                    alignItems="center"
                                    columnGap={1}
                                    onClick={() => pathNavigateClick(items.pathValue)}
                                >
                                    <IconButton
                                        className={activeMenu === items.pathValue ? 'active' : ''}
                                        sx={{ display: 'block' }}
                                    >
                                        {items.icon}
                                    </IconButton>
                                    {matches && (
                                        <Typography
                                            sx={{ cursor: 'pointer' }}
                                            variant="subtitle1"
                                            fontFamily="good_sansmedium"
                                        >
                                            {items.title}
                                        </Typography>
                                    )}
                                </Box>
                            </Tooltip>
                        ))}
                    </Stack>
                </Box>
                <Box className="logoutItem" display="flex" justifyContent="center">
                    <Tooltip title={t('logout')} arrow placement="right">
                        <Box
                            display={{ xs: 'flex', lg: 'block' }}
                            mb={{ xs: 2, lg: 0 }}
                            alignItems="center"
                            columnGap={1}
                            onClick={() => setModalOpen(true)}
                        >
                            <IconButton>
                                <LogoutIcon />
                            </IconButton>
                            {matches && (
                                <Typography sx={{ cursor: 'pointer' }} variant="subtitle1" fontFamily="good_sansmedium">
                                    {t('logout')}
                                </Typography>
                            )}
                        </Box>
                    </Tooltip>
                </Box>
                <CommonModal
                    modalOpen={modalOpen}
                    header={
                        <Stack rowGap={1} pb={1}>
                            <Typography variant="h5" color="secondary" textAlign="center">
                                {t('confirm_logout')}
                            </Typography>
                        </Stack>
                    }
                    cancelBtnName={t('cancel')}
                    AcceptBtnName={t('yes')}
                    closePopup={() => setModalOpen(false)}
                    acceptBtn={handleLogout}
                >
                    {t('logout_text')}
                </CommonModal>
            </SideMenuStyles>
        );
    };
    return matches ? (
        <Drawer open={menuOpen} onClose={menuClose}>
            {sidemenuContent()}
        </Drawer>
    ) : (
        sidemenuContent()
    );
};
